import {
  Box2,
  Clock,
  Color,
  Matrix3,
  Mesh,
  Object3D,
  PlaneBufferGeometry,
  Raycaster,
  Scene,
  Vector2,
  Vector3,
  Vector4,
  WebGLRenderer
} from 'three';
import {SphereItems} from './sphere_items';
import {Planogram} from './planogram';
import LodProvider from 'shared/lod/LodProvider';
import {pickDeviceLimits} from 'shared/lod/helpers';
import {MAX_ITEM_RENDER_ORDER, SphereItem} from './sphere_item';
import {ExtraImageData, ImageId, ImageLodData, LodId, LodLevelData, LodSource} from 'shared/lod/interfaces';
import {isLodItem} from './utils/planogram_utils';
import PlanogramPoint, {flipEditorY} from 'shared/utils/PlanogramPoint';
import loadingProgress, {LOADING_STAGES} from './api/services/loading_progress.service';
import {ImageMetaData, ItemData, ProductMetaData} from './interfaces/planogram.interface';
import {BackgroundImageWithLod, Gradient} from 'shared/interfaces/planogram';
import {backgroundDataToLodItem, createLodId, parseLodId, skipAtlasLevels} from 'shared/lod/helpers';
import {disposeObject3D} from './utils/disposeThree';
import {SphereItemType} from 'shared/interfaces/planogram';
import {debugFloatPrameter, debugLog} from 'shared/utils/debug';
import SphereCamera from 'shared/renderingEngine/SphereCamera';
import cameraConfig from 'shared/config/CameraConfig';
import {packShading} from 'shared/utils/format';
import {resolvePaletteColor} from 'shared/utils/colors';
import {sphereRenderingEngine} from 'shared/renderingEngine/SphereRenderingEngine';
import SphereCameraControls from 'shared/renderingEngine/CameraControls';
import {shiftObjectsCloserToCamera} from 'shared/utils/CameraUtils';
import {groupOrder, makeOverflowMeshes} from 'shared/utils/three-utils';
import {SphereShape} from 'shared/utils/SphereShape';
import {ViewableLimits} from 'shared/renderingEngine/ViewableLimits';
import GradientMaterial from 'shared/renderingEngine/materals/GradientMaterial';
import {showLoadingScreen} from './utils/loading_screen_utils';

const MAX_LOD_LOADING_DURATION = debugFloatPrameter('MAX_LOD_LOADING_DURATION', 1700);
const BASE_BLUR_SIZE = 29; // hand picked to match previous blur implementation

// using full device pixel ratio can put the renderer resolution over the max allowed texture size on some devices, which crashes post-processing
function limitDevicePixelRatio(renderer: WebGLRenderer) {
  const maxTextureSize = renderer.capabilities.maxTextureSize;
  const size = renderer.getSize(new Vector2());
  return Math.min(window.devicePixelRatio, maxTextureSize / size.x, maxTextureSize / size.y);
}

function getItemId(item: SphereItem): string {
  return item.id as string;
}

function getLodId(item: SphereItem): LodId {
  const pictureData = item.data as ProductMetaData;
  const id = pictureData.picture?.id ?? pictureData.id;
  if (pictureData.isFigma) {
    return createLodId(id, LodSource.FIGMA);
  } else {
    return createLodId(id, LodSource.DEFAULT);
  }
}

function measureCanvasSize() {
  // TODO: set canvas size with styles instead, measure renderer.domElement's size here
  return new Vector2(window.innerWidth, window.innerHeight);
}

export default class CanvasRenderer {
  // TODO: make private
  readonly camera: SphereCamera;
  readonly cameraControls: SphereCameraControls;

  private cameraListener: () => void | undefined;
  private viewableLimits: ViewableLimits;

  private scene: Scene;
  private renderer: WebGLRenderer;
  private lodPaused = false;
  private clock = new Clock();
  // TODO: make private, pass it to items here
  public lodProvider: LodProvider;

  get capabilities() {
    return this.renderer.capabilities;
  }

  get domElement() {
    return this.renderer.domElement;
  }

  private sphereShape: SphereShape;

  constructor(private canvas: HTMLCanvasElement, private planogram: Planogram) {
    this.sphereShape = new SphereShape(Planogram.ALPHA, 3, this.planogram.width);
    {
      this.scene = new Scene();
      this.scene.background = new Color(resolvePaletteColor(planogram.backgroundColor, planogram.palette));
    }

    this.renderer = new WebGLRenderer({
      canvas,
      alpha: true
    });
    // Shader error checks read shader/program info logs after every compile
    // On a lost WebGL context those return null and three.js crashes
    this.renderer.debug.checkShaderErrors = !IS_PRODUCTION;

    canvas.addEventListener('webglcontextlost', this.contextLostListener);
    canvas.addEventListener('webglcontextrestored', this.contextRestoredListener);

    {
      this.viewableLimits = new ViewableLimits(planogram.topLimit, planogram.bottomLimit);
      const startPoint = (this.planogram.startPoint / this.planogram.width) * 360.0;

      sphereRenderingEngine.init(this.scene, measureCanvasSize(), planogram.cameraPosition, 1, startPoint);
      this.cameraControls = sphereRenderingEngine.cameraControls;
      this.camera = this.cameraControls.sphereCamera;
      sphereRenderingEngine.cameras2D.addEdgeListener(cameraPosition => this.shiftItemsToCamera(cameraPosition));
    }

    this.updateRendererSize();

    {
      this.lodProvider = new LodProvider(this.renderer, {
        cdnUrl: CDN_HOST,
        ...pickDeviceLimits()
      });
      const canvasToPlanogram = canvas.height / this.viewableLimits.getLimitHeight();
      // Initiate camera parameters before rendering starts, so that we can run LOD preload
      this.lodProvider.updateCamera(this.camera.planogramPoint(), canvasToPlanogram);
    }

    {
      this.cameraListener = () => {
        const canvasToPlanogram = canvas.height / this.viewableLimits.getLimitHeight();
        const focusPoint = this.camera.planogramPoint();
        this.lodProvider.updateCamera(focusPoint, this.camera.zoomRatio() * canvasToPlanogram);
        const viewport = this.camera.computeViewport();
        this.viewportListeners.forEach(it => it(viewport));
      };
      this.camera.addEventListener('Updated', this.cameraListener);
    }
  }

  private shiftItemsToCamera(cameraPosition: Vector3) {
    shiftObjectsCloserToCamera(
      this.items.reduce<Object3D[]>((list, it) => {
        if (it.object3D !== undefined) list.push(it.object3D);
        return list;
      }, []),
      cameraPosition
    );
  }

  private makeBackgroundItem(id: string, imageId: number, backgroundImage: BackgroundImageWithLod) {
    const imageData: ImageMetaData = {
      id: imageId,
      image_name: id,
      lods_version: 0,
      naturalResolution: [backgroundImage.original_width, backgroundImage.original_height],
      thumbnails: backgroundImage.thumbnails,
      opacity: 1.0,
      fit_size: [backgroundImage.original_width, backgroundImage.original_height],
      full_size: [backgroundImage.original_width, backgroundImage.original_height]
    };
    const planogramSize = this.planogram.size();
    const backgroundItem = new SphereItem(
      {
        id: id,
        name: 'Background',
        x: 0,
        layer: MAX_ITEM_RENDER_ORDER,
        y: -planogramSize.y,
        type: SphereItemType.Image,
        width: planogramSize.x,
        height: planogramSize.x,
        data: imageData,
        lods: [], // otherwise SphereItem constructor makes it invisible
        visible: true
      },
      this.planogram,
      this.lodProvider
    );
    return backgroundItem;
  }

  private items: SphereItem[] = [];

  addItems(itemsGroup: SphereItems) {
    this.items = itemsGroup.items;
    this.scene.add(itemsGroup);
    this.shiftItemsToCamera(sphereRenderingEngine.cameras2D.mainCamera.position);
    const images: Map<ImageId, ExtraImageData> = new Map();
    const lodData: Map<string | number, ImageLodData> = new Map();
    itemsGroup.items.forEach(item => {
      if (!isLodItem(item.itemData)) return;
      {
        const material = this.lodProvider.getMaterial(getItemId(item));
        if (item.type === SphereItemType.Image || item.type === SphereItemType.Product) {
          const imageData = item.data as ImageMetaData;
          if (imageData.cropOffset !== undefined)
            material.setItemUniforms({cropOffset: {value: new Vector2(...imageData.cropOffset)}});
          if (imageData.cropRatio !== undefined)
            material.setItemUniforms({cropRatio: {value: new Vector2(...imageData.cropRatio)}});
        }
      }
      const itemData = item.itemData as ItemData<ImageMetaData>;
      const lodId: LodId = getLodId(item);
      const itemId = getItemId(item);

      images.set(itemId, {
        lodId,
        position: new PlanogramPoint(item.getViewportCenter()),
        size: item.getSize(),
        naturalResolution: new Vector2(...itemData.data?.naturalResolution),
        shading: packShading(itemData.data.shading, itemData.data.opacity, this.planogram.palette),
        cornerRadius: new Vector4(...(itemData.data.cornerRadius ?? [0, 0, 0, 0]))
      });

      const filteredLods = skipAtlasLevels(itemData.lods).filter((lod, i) => {
        function atlasSize(lod: LodLevelData) {
          return lod.textures[0]?.uv?.width ?? 1.0;
        }
        if (i === 0) return true;
        if (atlasSize(lod) === 1.0) return true;
        // TODO: ask BE to filter out identical atlas LOD levels
        return atlasSize(itemData.lods[i - 1]) !== atlasSize(lod);
      });

      lodData.set(lodId, {
        id: parseLodId(lodId).id,
        curator_lods: filteredLods,
        fit_size: itemData.data.fit_size,
        full_size: itemData.data.full_size,
        naturalResolution: itemData.data.naturalResolution,
        lods_version: itemData.data.lods_version
      });
    });
    const backgroundGradient: Gradient | undefined = this.planogram.backgroundGradient;
    const backgroundImage: BackgroundImageWithLod | undefined = this.planogram.background_images[0];
    if (backgroundImage !== undefined) {
      const BACKGROUND_ID = 'generated-background';
      const BACKGROUND_IMAGE_ID = -1;
      const backgroundItem = this.makeBackgroundItem(BACKGROUND_ID, BACKGROUND_IMAGE_ID, backgroundImage);
      this.scene.add(backgroundItem.object3D);
      makeOverflowMeshes(backgroundItem.object3D.userData.mesh);

      const lodId = createLodId(backgroundImage.id, LodSource.BACKGRDOUND);
      images.set(BACKGROUND_ID, {
        lodId,
        position: new PlanogramPoint(backgroundItem.getViewportCenter()),
        size: this.planogram.size(),
        naturalResolution: new Vector2(backgroundImage.original_width, backgroundImage.original_height),
        shading: new Vector4(1, 1, 1, 1),
        cornerRadius: new Vector4(0, 0, 0, 0)
      });
      lodData.set(lodId, backgroundDataToLodItem(backgroundImage));
    } else if (backgroundGradient !== undefined) {
      const material = new GradientMaterial(backgroundGradient.points.length);
      const matrix = new Matrix3();
      const width = this.planogram.width;
      matrix.scale(width, width);
      matrix.translate(0.5 * width, flipEditorY(0.5 * width));
      material.update(backgroundGradient, matrix, this.planogram.palette);

      const geometry = new PlaneBufferGeometry(1, 1);
      const leftMesh = new Mesh(geometry, material);
      leftMesh.position.set(0.5 * width, 0, 0);
      leftMesh.scale.set(width, width, 1);
      const rightMesh = leftMesh.clone();
      rightMesh.position.x -= width;
      this.scene.add(leftMesh, rightMesh);
    }
    this.lodProvider.updateImageList(images, (ids: (number | string)[]) =>
      Promise.resolve(ids.map(id => lodData.get(id)))
    );

    debugLog(this.scene.children);
  }

  preloadLod() {
    return new Promise<void>(resolve => {
      this.renderer.setAnimationLoop(() => this.lodProvider.update());
      let timeout: NodeJS.Timeout;
      const finish = () => {
        loadingProgress.progressStage(LOADING_STAGES.TILES_FOR_LOD, 1);
        this.lodProvider.removeLoadedListener(finish);
        this.renderer.setAnimationLoop(null);
        clearTimeout(timeout);
        resolve();
      };
      timeout = setTimeout(finish, MAX_LOD_LOADING_DURATION);
      this.lodProvider.addLoadedListener(finish);
      // normally LOD updates are spread over frames to minimize FPS hit
      // during initial loading it doesn't matter, so we update as often as possible to speed up loading
    });
  }

  // scale used to mean resolution scale for kawase filter implemented by the postprocessing package
  blur(scale?: number) {
    if (scale === undefined) this.canvas.style.filter = '';
    else {
      const kernelSize = (BASE_BLUR_SIZE * scale) / 0.5;
      this.canvas.style.filter = `blur(${kernelSize.toFixed(3)}px)`;
    }
  }

  start() {
    this.updateRendererSize();
    this.renderer.setAnimationLoop(() => this.render());
  }

  stop() {
    this.renderer.setAnimationLoop(null);
  }

  pauseLodUpdates(state: boolean) {
    this.lodPaused = state;
  }

  private updateRendererSize() {
    const canvasSize = measureCanvasSize();
    this.camera.canvasSize = canvasSize;
    const pixelRatio = limitDevicePixelRatio(this.renderer);
    sphereRenderingEngine.canvasSize = canvasSize;
    this.renderer.setSize(canvasSize.x, canvasSize.y);
    this.renderer.setPixelRatio(pixelRatio);
  }

  resize() {
    this.updateRendererSize();
    this.renderer.setRenderTarget(null);
    this.camera.zoomTo(cameraConfig.FOV_DEFAULT);
    this.render();
  }

  private viewportListeners: Array<(viewport: Box2) => void> = [];

  onViewportChange(callback: (viewport: Box2) => void) {
    this.viewportListeners.push(callback);
  }

  private render() {
    if (!this.lodPaused) {
      this.lodProvider.update();
    }
    const dt = this.clock.getDelta();
    sphereRenderingEngine.render(this.renderer);
    this.renderListeners.forEach(it => it(dt));
  }

  private renderListeners: Array<(dt: number) => void> = [];

  onRender(callback: (dt: number) => void) {
    this.renderListeners.push(callback);
  }

  private contextRestoredListeners: Array<() => void> = [];

  // Called once the browser has given us a new WebGL context. All GPU resources (LOD atlases, render targets,
  // shaders) are gone at that point, so the owner is expected to rebuild the sphere from scratch.
  onContextRestored(callback: () => void) {
    this.contextRestoredListeners.push(callback);
  }

  private contextLostListener = (event: Event) => {
    // Without preventDefault the browser never fires `webglcontextrestored`
    event.preventDefault();
    console.warn('WebGL context lost, waiting for the browser to restore it');
    this.stop();
    showLoadingScreen(true);
  };

  private contextRestoredListener = () => {
    console.warn('WebGL context restored, rebuilding the sphere');
    this.contextRestoredListeners.forEach(it => it());
  };

  getInteractableObjectsAtScreenCoordinate(x: number, y: number): {meshes: Object3D[]; point: Vector3} {
    const origin3d = this.camera.getMouseIntersection(x, y);
    const spherePoint = this.sphereShape.reverse(origin3d);
    const planogramPoint = new PlanogramPoint(
      this.sphereShape.planogramCoordinateViewer(spherePoint, this.planogram.size())
    );
    // TODO: why is offset necessary? .planogramCoordinate is off on X axis
    planogramPoint.sub({x: 0, y: 0.25 * this.planogram.height});
    const camera2dPosition = sphereRenderingEngine.cameras2D.mainCamera.position;
    const origin2d = planogramPoint.toVectorAround(new Vector3(), camera2dPosition);
    origin2d.z = camera2dPosition.z;
    const caster2d = new Raycaster(origin2d, new Vector3(0, 0, -1));
    caster2d.layers.enableAll();
    const intersects = caster2d.intersectObjects(this.scene.children, true);
    const sortedIntersections = intersects
      .map(it => it.object)
      .sort((a, b) => {
        const groupDiff = groupOrder(b) - groupOrder(a);
        if (groupDiff !== 0) return groupDiff;
        const renderDiff = b.renderOrder - a.renderOrder;
        if (renderDiff !== 0) return renderDiff;
        else return b.id - a.id;
      });

    return {meshes: sortedIntersections, point: origin3d};
  }

  dispose() {
    this.lodProvider.dispose();
    this.stop();
    this.canvas.removeEventListener('webglcontextlost', this.contextLostListener);
    this.canvas.removeEventListener('webglcontextrestored', this.contextRestoredListener);
    this.viewportListeners.splice(0, this.viewportListeners.length);
    this.renderListeners.splice(0, this.renderListeners.length);
    this.contextRestoredListeners.splice(0, this.contextRestoredListeners.length);
    this.camera.removeEventListener('Updated', this.cameraListener);

    disposeObject3D(this.scene);

    sphereRenderingEngine.dispose();
  }
}
